import React, { useEffect, useMemo, useState } from 'react';
import { RichTextContent } from '../shared/RichTextEditor';
import {
  LEGAL_DOC_TYPES,
  resolveLegalDocument,
  type LegalDocType,
} from '../../lib/legalConfig';
import { fetchPublicLegal, type PublicLegalView } from './legalApi';
import LegalFooter from './LegalFooter';
import { storage } from '../../lib/firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

/**
 * Public legal pages (/privacy, /terms, /refund-policy, /cookies,
 * /accessibility). Content comes from the workspace Legal config resolved
 * by host: uploaded active document first, then the rich-text body, then
 * the external link. When nothing is configured the page renders a
 * NEUTRAL unbranded state — never another brand's content and never a
 * hardcoded fallback brand.
 */
/** Published-file view: resolves a download URL from Storage (public
 *  legal slots are world-readable in storage.rules) and links it. */
function DocumentFileView({ fileName, storagePath, version }: { fileName: string; storagePath: string; version: number }) {
  const [url, setUrl] = useState<string>('');
  useEffect(() => {
    let alive = true;
    if (!storagePath) return;
    getDownloadURL(storageRef(storage, storagePath))
      .then(u => { if (alive) setUrl(u); })
      .catch(() => {});
    return () => { alive = false; };
  }, [storagePath]);
  return (
    <div className="text-sm space-y-3">
      <p>This document is published as a file:</p>
      {url ? (
        <p>
          <a className="text-brand underline font-semibold" href={url} target="_blank" rel="noopener noreferrer">
            {fileName}
          </a>
        </p>
      ) : (
        <p className="font-semibold text-slate-700">{fileName}</p>
      )}
      <p className="text-xs text-slate-400">Version {version}. Ask support for a copy if the file does not open.</p>
    </div>
  );
}

export function LegalDocumentPage({ docType }: { docType: LegalDocType }) {
  const [view, setView] = useState<PublicLegalView | null>(null);
  const meta = LEGAL_DOC_TYPES.find(d => d.key === docType)!;

  useEffect(() => {
    let alive = true;
    fetchPublicLegal().then(v => { if (alive) setView(v); });
    return () => { alive = false; };
  }, []);

  const resolved = useMemo(() => {
    if (!view) return null;
    return resolveLegalDocument(
      {
        workspaceId: view.workspaceId || '',
        links: view.links,
        privacyContactEmail: view.privacyContactEmail,
        postalAddress: view.postalAddress,
        documents: view.documents,
        disclosures: view.disclosures,
        documentSlots: view.documentSlots,
      },
      docType,
    );
  }, [view, docType]);

  return (
    <div className="min-h-dvh bg-white text-slate-800 font-sans">
      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{meta.label}</h1>
        {!view || !resolved ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : resolved.source === 'body' ? (
          <article className="prose-sm max-w-none">
            <RichTextContent html={resolved.html || ''} />
            <p className="text-xs text-slate-400 mt-8">Version {resolved.version}</p>
          </article>
        ) : resolved.source === 'document' ? (
          <DocumentFileView fileName={resolved.fileName || meta.label} storagePath={resolved.storagePath || ''} version={resolved.version} />
        ) : resolved.source === 'link' ? (
          <p className="text-sm">
            This document is published at{' '}
            <a className="text-brand underline" href={resolved.url} target="_blank" rel="noopener noreferrer">
              {resolved.url}
            </a>
            .
          </p>
        ) : (
          <div className="text-sm space-y-3" role="status">
            <p>This document has not been published yet.</p>
            {view.privacyContactEmail ? (
              <p>
                For questions in the meantime, contact{' '}
                <a className="text-brand underline" href={`mailto:${view.privacyContactEmail}`}>
                  {view.privacyContactEmail}
                </a>
                .
              </p>
            ) : (
              <p>Please contact support through the portal for questions in the meantime.</p>
            )}
          </div>
        )}
        <div className="mt-12 border-t border-slate-100 pt-5">
          <LegalFooter />
        </div>
      </main>
    </div>
  );
}

/** Public privacy-request page (data rights: access / deletion / correction / portability). */
export function PrivacyRequestPage() {
  const [view, setView] = useState<PublicLegalView | null>(null);
  const [type, setType] = useState('access');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPublicLegal().then(setView);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/legal/privacy-requests/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, email, name, message, workspaceId: view?.workspaceId || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setDone(data.slaDueAt ? `Your request was received. We respond within 30 days (by ${new Date(data.slaDueAt).toLocaleDateString()}). We may contact you to verify your identity first.` : 'Your request was received.');
    } catch (err: any) {
      setError(err.message || 'Submission failed. Please try again.');
    }
  };

  const inputCls = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white';
  return (
    <div className="min-h-dvh bg-white text-slate-800 font-sans">
      <main className="max-w-xl mx-auto px-5 py-10">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Privacy Request</h1>
        <p className="text-sm text-slate-600 mb-6">
          Ask for a copy of your information, a correction, deletion, or to withdraw consent. If you have an
          account, signing in and using the form in your account settings verifies your identity automatically.
        </p>
        {done ? (
          <p className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3" role="status">{done}</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-xs font-semibold text-slate-600">
              Request type
              <select className={inputCls} value={type} onChange={e => setType(e.target.value)}>
                <option value="access">Access — show me my information</option>
                <option value="portability">Portability — send me a copy</option>
                <option value="correction">Correction — fix something wrong</option>
                <option value="deletion">Deletion — delete my information</option>
                <option value="withdraw_consent">Withdraw consent</option>
                <option value="limit_use">Limit use of sensitive information</option>
              </select>
            </label>
            <label className="block text-xs font-semibold text-slate-600">
              Your name
              <input className={inputCls} value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
            </label>
            <label className="block text-xs font-semibold text-slate-600">
              Email used with us
              <input className={inputCls} type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
            </label>
            <label className="block text-xs font-semibold text-slate-600">
              Details
              <textarea className={inputCls} rows={4} value={message} onChange={e => setMessage(e.target.value)} />
            </label>
            {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
            <button type="submit" className="w-full py-2.5 btn-primary rounded-xl text-sm font-semibold cursor-pointer">
              Submit request
            </button>
          </form>
        )}
        <div className="mt-12 border-t border-slate-100 pt-5">
          <LegalFooter />
        </div>
      </main>
    </div>
  );
}

/** Public unsubscribe page — writes the suppression record (CAN-SPAM). */
export function UnsubscribePage() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/legal/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed');
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Failed. Please try again.');
    }
  };
  return (
    <div className="min-h-dvh bg-white text-slate-800 font-sans">
      <main className="max-w-md mx-auto px-5 py-10">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Unsubscribe</h1>
        {done ? (
          <p className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3" role="status">
            You are unsubscribed. You will no longer receive marketing emails from this workspace. Emails about an
            active application or account may still be sent.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-slate-600">Enter the email address to unsubscribe from marketing emails.</p>
            <input
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white"
              type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
              aria-label="Email address"
            />
            {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
            <button type="submit" className="w-full py-2.5 btn-primary rounded-xl text-sm font-semibold cursor-pointer">
              Unsubscribe
            </button>
          </form>
        )}
        <div className="mt-12 border-t border-slate-100 pt-5">
          <LegalFooter />
        </div>
      </main>
    </div>
  );
}
